import _ from 'lodash';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';

import copilot from '@janus.team/janus-copilot/cjs/janus-copilot';
import { useQueryGetBillingConfig } from 'hooks/queries';

export const googleOauth = React.createContext({});

/**
 * RackspaceAccountContext provides combined details for several bits of account data
 * from the session and CMS (via Copilot), Janus Billing, and other sources.
 *
 * See RackspaceAccountProvider for implementation.
 */
export const RackspaceAccountContext = React.createContext({
  accountName: null,
  rackspaceAccountNumber: null,
  cms: {},

  billing: {
    aws: {},
    azure: {},
    gcp: {
      infrastructureModel: 'resell',
      isResell: true,
      isNoResell: false,
      baseServiceBlocks: [],
      serviceModel: 'service-blocks',
    },
  },
});

export const useRackspaceAccountContext = () => React.useContext(RackspaceAccountContext);

export const RackspaceAccountProvider = ({ children }) => {
  const defaultContext = React.useContext(RackspaceAccountContext);
  const { session } = React.useContext(copilot.context.session);
  const customer = session?.session?.customer;
  const saml = session?.session?.saml;
  const match = useRouteMatch('/racker/customers/:rackspaceAccountNumber');
  const rackerMatch = global.isRacker && _.get(match, ['params', 'rackspaceAccountNumber']);
  const rackspaceAccountNumber = _.get(saml, ['account_id'], rackerMatch);
  const accountName = saml?.account_name;

  const janusBillingConfigQueryOptions = {
    enabled: !!rackspaceAccountNumber,
    staleTime: Infinity,
    onSuccess: (data) => {
      const infrastructureModel = _.get(data, 'configuration.accountType.MANAGED_GCP.infrastructureModel', 'resell');
      const isResell = infrastructureModel === 'resell';
      const baseServiceBlocks = _.get(data, 'configuration.accountType.MANAGED_GCP.baseServiceBlocks', []);
      const serviceModel = _.get(data, 'configuration.accountType.MANAGED_GCP.serviceModel', 'service-blocks');
      _.set(defaultContext, 'billing.gcp', {
        infrastructureModel,
        isResell,
        isNoResell: !isResell,
        baseServiceBlocks,
        serviceModel,
      });
    },
  };
  useQueryGetBillingConfig(rackspaceAccountNumber, janusBillingConfigQueryOptions);

  const rackspaceAccountContext = _.merge({}, defaultContext, {
    accountName,
    rackspaceAccountNumber,
    cms: customer,
  });
  return (
    <RackspaceAccountContext.Provider value={rackspaceAccountContext}>
      {children}
    </RackspaceAccountContext.Provider>
  );
};
